/**
 * EmissiveMaterial — BL-26 (ART §6.1 emissive core->mid->rim) and BL-27
 * (ART §6.2 rim-light / fake-normals) unified in ONE offset-radial gradient.
 *
 * The gradient's hot inner circle is nudged toward the light (top-left), so the
 * near-white `core` hot-spot sits off-centre. That single offset buys both
 * effects at once:
 *   - core -> mid -> rim falloff      = emissive material (BL-26)
 *   - off-centre hot-spot + dark rim  = directional volume / fake-normal (BL-27)
 *
 * The `core` stop is > 0.5 luminance, so the existing WebGL bloom pass
 * (WebGL2Effects BLOOM_EXTRACT) extracts and amplifies it — that is the actual
 * glow. This module deliberately adds NO blur of its own; it only emits the
 * emissive ramp and lets the shipped bloom do the lifting.
 *
 * Shape-agnostic: the caller builds its own path and fills it with the gradient
 * returned by `emissiveFill`. No assumptions about the silhouette are made, so
 * the same helper lights tower nodes, the enemy body, the projectile head, and
 * any future per-enemy silhouettes (BL-51).
 */

export interface Ramp {
  core: string;
  mid: string;
  rim: string;
}

// ─── Tuning knobs (this is a LOOK ticket — tune these in-engine) ────────────
/** Hot-spot offset toward top-left, in units of the body radius. */
const LIGHT_OFFSET = 0.34;
/** Gradient outer radius as a multiple of the body radius (slight overscan). */
const RADIUS_SCALE = 1.05;
/** Position of the identity `mid` stop along the core->rim ramp. */
const MID_STOP = 0.5;

// ─── Hex helpers ────────────────────────────────────────────────────────────

function parseHex(hex: string): [number, number, number] {
  let h = hex.replace(/^#/, "");
  if (h.length === 3) h = h.replace(/(.)/g, "$1$1");
  const n = parseInt(h, 16);
  return [(n >> 16) & 0xff, (n >> 8) & 0xff, n & 0xff];
}

function channelHex(v: number): string {
  const c = Math.max(0, Math.min(255, Math.round(v)));
  return c.toString(16).padStart(2, "0");
}

/** Per-channel linear mix: t=0 -> a, t=1 -> b. Returns a #rrggbb string. */
function mix(a: string, b: string, t: number): string {
  const [ar, ag, ab] = parseHex(a);
  const [br, bg, bb] = parseHex(b);
  return `#${channelHex(ar + (br - ar) * t)}${channelHex(
    ag + (bg - ag) * t,
  )}${channelHex(ab + (bb - ab) * t)}`;
}

/**
 * Public per-channel hex mix (t=0 -> a, t=1 -> b). The shared building block for
 * a multi-stop colour ramp built FROM central palette anchors (e.g. the BL-63
 * wave heat ramp green→brass→red) — so callers never hardcode the in-between
 * hexes (I4 "one colour source").
 */
export function mixHex(a: string, b: string, t: number): string {
  return mix(a, b, t < 0 ? 0 : t > 1 ? 1 : t);
}

// ─── deriveRamp ─────────────────────────────────────────────────────────────

const rampCache = new Map<string, Ramp>();

/**
 * Derive a {core, mid, rim} emissive ramp from a single base colour, for
 * entities that have no ELEMENT_RAMP entry (enemies, projectiles).
 *
 * This is the exact transform that generated ELEMENT_RAMP in designTokens.ts:
 *   core = base -> white 60%   rim = base -> black 72%
 * so `deriveRamp(ELEMENT_COLORS[el])` deep-equals `ELEMENT_RAMP[el]`.
 *
 * Memoised by base colour — the live entity colour set is small and fixed, so
 * the hex maths runs once per distinct colour, not once per entity per frame.
 */
export function deriveRamp(mid: string): Ramp {
  const cached = rampCache.get(mid);
  if (cached) return cached;
  const ramp: Ramp = {
    core: mix(mid, "#ffffff", 0.6),
    mid,
    rim: mix(mid, "#000000", 0.72),
  };
  rampCache.set(mid, ramp);
  return ramp;
}

// ─── emissiveFill ───────────────────────────────────────────────────────────

/**
 * Build an offset-radial emissive gradient for a body centred at (cx, cy) with
 * body radius `r`. The caller fills its own (already-built) path:
 *
 *   ctx.fillStyle = emissiveFill(ctx, cx, cy, r, ramp);
 *   ctx.fill();
 *
 * The hot core is offset toward the top-left by `r * LIGHT_OFFSET`; beyond the
 * outer radius the gradient clamps to `rim`, so silhouette tips read as dark
 * (reinforcing the volume).
 */
export function emissiveFill(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  r: number,
  ramp: Ramp,
): CanvasGradient {
  const off = r * LIGHT_OFFSET;
  const grad = ctx.createRadialGradient(
    cx - off,
    cy - off,
    0,
    cx,
    cy,
    r * RADIUS_SCALE,
  );
  grad.addColorStop(0, ramp.core);
  grad.addColorStop(MID_STOP, ramp.mid);
  grad.addColorStop(1, ramp.rim);
  return grad;
}
